import React, { useEffect } from 'react';
import appConfig from './config';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/layout/Layout';
import Users from './pages/Users';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/Login';
import Wallet from './components/WalletUpdate';
import Deposit from './pages/Deposit';
import Withdraw from './pages/Withdraw';
import House from './pages/House'
import History from './pages/History';
import Game from './pages/Games';
import GamesLog from './pages/WinSetting';
import SEO from './pages/SeoManager';
import Referral from './pages/Referral';
import NotificationSettings from './pages/NotificationSettings';
import WinSettings from './pages/WinSetting';
import Payments from './pages/Payment';
import ManualDeposit from './pages/ManualDeposit';
import Withdrawals from './pages/Withdrawals';
import ChangePassword from './pages/ChangePassword';
import GameControl from './pages/GameControl';
import SubAdminManage from './pages/SubAdminManage';
import SubAdminUser from './pages/SubAdminUser';

import CollaboratorUser from './pages/CollaboratorUser';
import UserProfile from './pages/UserProfile';
import UserReports from './pages/UserReports';
import FinancialReports from './pages/FinancialReports';
import GameHistoryReports from './pages/GameHistoryReports';
import ProfitAnalytics from './pages/ProfitAnalytics';
import PendingCollaboratorUser from './pages/PendingCollaboratorUser';
import Aggregators from './pages/GameEngine/Aggregators';
import Providers from './pages/GameEngine/Providers';
import Groups from './pages/GameEngine/Groups';
import Categories from './pages/GameEngine/Categories';
import GamesManagement from './pages/GameEngine/GamesManagement';
import AuthConfigs from './pages/AuthConfigs';
import GatewayConfigs from './pages/GatewayConfigs';
import CampaignBuilder from './pages/CampaignBuilder';
import CmsPageManager from './pages/CmsPageManager';
import AffiliateSettings from './pages/AffiliateSettings';
import VipLevels from './pages/VipLevels';
import AffiliatePayoutRules from './pages/AffiliatePayoutRules';
import AffiliatePayoutReport from './pages/AffiliatePayoutReport';
import AffiliatePayoutReportDetails from './pages/AffiliatePayoutReportDetails';

import axios from 'axios';

import apiClient from './services/apiClient';

// Setup global axios interceptor for admin refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (originalRequest.url && (originalRequest.url.includes('/admin/login') || originalRequest.url.includes('/login'))) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        try {
          const res = await axios.post(`${appConfig.baseURL}/api/auth/refresh`, {
            refreshToken
          });
          
          if (res.data && res.data.token) {
            const newToken = res.data.token;
            localStorage.setItem('token', newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        } catch (err) {
          console.error('Refresh token failed:', err);
        }
      }
      // If we reach here, either there was no refresh token, or the refresh request failed
      const role = (localStorage.getItem('role') || '').toLowerCase();
      localStorage.clear();
      if (role === 'affiliate') {
        window.location.href = '/login/affiliate';
      } else if (role === 'subadmin' || role === 'manager') {
        window.location.href = '/login/manager';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const token = localStorage.getItem('token');

  // ── Fetch brand config from DB on startup ──────────────────────────────
  useEffect(() => {
    fetch(`${appConfig.baseURL}/website-config`)
      .then(r => r.json())
      .then((data: any) => {
        if (!data) return;
        const configData = data.success ? data.data : data;

        // Store globally so any component can access
        (window as any).adminSiteConfig = configData;

        // Set page title
        if (configData.website_name && !window.location.pathname.startsWith('/login')) {
          const role = (localStorage.getItem('role') || 'Admin').toLowerCase();
          const roleDisplayName = role === 'admin' ? 'Admin' : role.charAt(0).toUpperCase() + role.slice(1);
          document.title = `${configData.website_name} — ${roleDisplayName}`;
        }

        // Set favicon
        if (configData.favicon) {
          const faviconUrl = `${appConfig.baseURL}${configData.favicon}?t=${Date.now()}`;
          let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = faviconUrl;
        }
      })
      .catch(() => {});
  }, []);

  // ── Real-time collaborator block/status check polling ─────────────────
  useEffect(() => {
    if (!token) return;

    const checkStatus = async () => {
      try {
        // Send a request to check if user is still active in DB
        await apiClient.get('/collaborator/check-status');
      } catch (err: any) {
        console.error('Status check error:', err);
      }
    };

    // Check status immediately
    checkStatus();

    // Check status every 10 seconds
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [token]);
  // ───────────────────────────────────────────────────────────────────────

  const ProtectedRoutes = () => {
    return token ? <Outlet /> : <Navigate to="/login" replace />;
    //  return <Outlet/>
  };

  return (
    <BrowserRouter>
      <ToastContainer 
        theme="dark" 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={true} 
        transition={Slide}
        closeButton={false}
        toastClassName="premium-toast"
      />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/:roleParam" element={<LoginPage />} />
        <Route element={<ProtectedRoutes />}>
          <Route path="campaigns/editor" element={<CampaignBuilder />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="users" element={<Users />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="users/profile/:id" element={<UserProfile />} />
            <Route path="settings" element={<Settings />} />
            <Route path="cms" element={<CmsPageManager />} />
            <Route path="subadmin" element={<SubAdminManage />} />
            <Route path="collaboratorUser" element={<CollaboratorUser />} />
            <Route path="pendingCollaboratorUser" element={<PendingCollaboratorUser />} />
            <Route path="subadminuser" element={<SubAdminUser />} />
            <Route path="changePassword" element={<ChangePassword />} />
            <Route path="withdrawals" element={<Withdrawals />} />
            <Route path="manual-withdrawal" element={<Navigate to="/withdrawals" replace />} />
            <Route path="deposits" element={<ManualDeposit />} />
            <Route path="deposit" element={<Deposit />} />
            <Route path="games" element={<Game />} />
            <Route path="win-settings" element={<WinSettings />} />
            <Route path="house" element={<House />} />
            <Route path="game-control" element={<GameControl />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="history" element={<History />} />
            <Route path="seo-manager" element={<SEO />} />
            <Route path="affiliate" element={<Referral />} />
            <Route path="affiliate-settings" element={<AffiliateSettings />} />
            <Route path="affiliate-payouts/rules" element={<AffiliatePayoutRules />} />
            <Route path="affiliate-payouts/report" element={<AffiliatePayoutReport />} />
            <Route path="affiliate-payouts/report/details/:affiliateId" element={<AffiliatePayoutReportDetails />} />
            <Route path="notification-settings" element={<NotificationSettings />} />
            <Route path="auth-configs" element={<AuthConfigs />} />
            <Route path="gateway-configs" element={<GatewayConfigs />} />
            <Route path="campaigns" element={<CampaignBuilder />} />
            <Route path="vip-levels" element={<VipLevels />} />

            {/* Reports & Analytics */}
            <Route path="reports/users" element={<UserReports />} />
            <Route path="reports/financials" element={<FinancialReports />} />
            <Route path="reports/game-history" element={<GameHistoryReports />} />
            <Route path="reports/profit" element={<ProfitAnalytics />} />

            <Route path="game-engine/aggregators" element={<Aggregators />} />
            <Route path="game-engine/providers" element={<Providers />} />
            <Route path="game-engine/categories" element={<Categories />} />
            <Route path="game-engine/groups" element={<Groups />} />
            <Route path="game-engine/games" element={<GamesManagement />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
